import { useEffect } from 'react';
import { useBadges } from '../api/badges';
import { useCollections } from '../api/collections';
import { useCelebrationQueue } from './celebrationQueue';

/**
 * Two of the three producers for the interrupt tier (`docs/DESIGN.md` §21, owner decisions D2/D3):
 * a first-time badge and a completed collection. The third — level-up — is raised by
 * `HabitatScreen`, which is where the companion whose level changed is actually observed.
 *
 * **Why this is a client-side diff, when §6.2 rejected exactly that for the item-arrival beat.**
 * The rejection there was specific and still stands: a bag list gains and loses rows for all sorts
 * of reasons, so "a row appeared since last time" fires on any refetch that happens to add one —
 * another device, a late async daily reward. Neither flag diffed here can do that, because **both
 * are monotonic**. `BadgeDto.awarded` is derived server-side from whether `awarded_at` is non-null
 * and a badge is never un-awarded; `CollectionSetDto.completed` likewise only ever goes
 * false→true. A diff over a value that cannot go backwards cannot false-positive on a refetch: the
 * only way a code can be absent from the baseline and present now is that it genuinely became true
 * in between. This is the same idiom `HabitatScreen`'s own level producer uses (`prevLevelRef`
 * against a monotonic level).
 *
 * **Why no server change is needed for §7.2's "first-time only".** The server already draws that
 * line: `BadgeService.advanceBadge` publishes `BadgeAwarded` only when the insert's row count is 1
 * (a genuine first-ever award), and `CollectionService` publishes `CollectionCompleted` only when
 * `inserted != 0`. Because the flags those writes produce are monotonic, the client sees the same
 * one-way edge without needing the event itself. A re-sync, a backfill or a re-grant leaves the
 * flag already true, so it is already in the baseline and produces nothing.
 *
 * **The first-load guard, which is the single most likely bug here.** A fresh mount arrives with
 * every badge the player has ever earned already `awarded: true`. Firing on that would produce a
 * modal per badge on every app boot. The baselines below therefore start as `null` — *unseeded*,
 * which is a genuinely different state from *seeded and empty* — and the FIRST payload to arrive
 * only ever seeds them. Nothing is raised until a second payload shows a code the first did not.
 *
 * **Why the baselines are module-level rather than refs.** `AppShell` is unmounted entirely while
 * a battle or a minigame owns the screen (both are router siblings of it — see `Celebration.tsx`),
 * and a battle is where badges are most often earned. A ref would reset on that remount, re-seed
 * the baseline from the post-battle payload, and silently swallow exactly the awards this exists to
 * celebrate. Module-level state survives the remount; the queue's own `blocked` handling is what
 * keeps the celebration from playing *during* the battle. Reseeding on a full page load is correct
 * and deliberate: a boot cannot know what happened while the tab was closed, so it must not guess.
 *
 * **Idempotent under `<StrictMode>`.** Each code is added to its baseline in the same statement
 * that raises it, so a double-invoked effect finds it already recorded and raises nothing the
 * second time. The guard is set membership, not a render-count or a data-reference comparison, so
 * it also holds across the unmount/remount `<StrictMode>` performs on mount.
 *
 * **What makes the data actually arrive.** Both queries are mounted here, at shell level, so they
 * are live observers rather than screen-scoped ones: a `badgesKeys.all` invalidation (battle end,
 * minigame submit) and a `collectionsKeys.all` invalidation (every item grant — see `shops.ts` and
 * `world.ts`) become real refetches, and the default `refetchOnWindowFocus` covers everything else.
 * Without a live observer an invalidation only marks the entry stale and nothing would ever be
 * diffed.
 */

/** Awarded badge codes as of the last payload seen. `null` = never seeded (see the first-load
 *  guard above); an empty Set = seeded, and the player genuinely has no badges yet. */
let seenBadges: Set<string> | null = null;
/** Completed collection-set codes as of the last payload seen. Same `null`-means-unseeded rule. */
let seenCollections: Set<string> | null = null;

/** Drops both baselines back to unseeded. Called for real on logout (`MeScreen`'s logout
 *  mutation, alongside a `queryClient.clear()` there) — the module-level state above is
 *  deliberately process-lifetime, not React-lifetime, so nothing else ever resets it, and
 *  without this call an account B logged into the same tab after account A logs out gets its
 *  own already-awarded badges/collections diffed against A's baseline, firing one full-screen
 *  celebration per badge/set B holds that A didn't — the exact first-load storm the first-load
 *  guard above exists to prevent, re-entering through a different door. Also used directly by
 *  tests that need a clean slate between cases (see `motionWiring.test.tsx`'s own `beforeEach`). */
export function resetAchievementBaselines(): void {
  seenBadges = null;
  seenCollections = null;
}

export function useAchievementCelebrations(): void {
  const badges = useBadges();
  const collections = useCollections();
  const request = useCelebrationQueue((s) => s.request);

  // The collection's effect runs FIRST: when one payload cycle completes a set AND awards its badge,
  // the set is the deed and the badge its reward, so the queue reads "Collection complete" then
  // "New badge", not the reverse (QA 2026-09-23, progress F17). Effects fire in declaration order.
  const collectionData = collections.data;
  useEffect(() => {
    const list = collectionData?.sets;
    if (!list) return;
    const completed = list.filter((s) => s.completed);
    if (seenCollections == null) {
      seenCollections = new Set(completed.map((s) => s.code));
      return;
    }
    for (const set of completed) {
      if (seenCollections.has(set.code)) continue;
      seenCollections.add(set.code);
      const pieces = set.pieces.length;
      request({
        kind: 'collection',
        title: set.name,
        sub: `All ${pieces} piece${pieces === 1 ? '' : 's'} collected.`,
      });
    }
  }, [collectionData, request]);

  const badgeData = badges.data;
  useEffect(() => {
    const list = badgeData?.badges;
    if (!list) return;
    const awarded = list.filter((b) => b.awarded);
    if (seenBadges == null) {
      seenBadges = new Set(awarded.map((b) => b.code));
      return;
    }
    for (const badge of awarded) {
      if (seenBadges.has(badge.code)) continue;
      seenBadges.add(badge.code);
      // `hint` is the badge's own authored one-liner and is the only prose the DTO carries — real
      // content, not a placeholder. The kicker ("New badge") is supplied by `Celebration` itself.
      request({ kind: 'badge', title: badge.name, sub: badge.hint });
    }
  }, [badgeData, request]);
}
